import { useQuery } from '@apollo/client'
import { Close } from '@mui/icons-material'
import { Drawer, IconButton, Stack, Typography } from '@mui/material'
import { GetRosterCandidateForClientProfileQuery } from '__generated__/graphql'
import { useIdentity, useRosterTable } from 'client/hooks'
import { getAuditLabel } from 'recruiter/pages/Rosters/RosterProfile/CandidateRosterNoteDrawer/RosterCandidateRecruiterNote'
import { Accordion } from 'shared/components/Accordion'
import { getPxAsRem } from 'shared/utils'
import { GetCandidateForClientProfile } from '../../graphql/candidate-drawer.gql'
import CandidateDetails from './CandidateDetails'
import ClientNotes from './ClientNotes'
import DownloadResume from './DownloadResume'
import OtherSearchesForCandidate from './OtherSearchesForCandidate'
import RosterCandidateFavorites, {
    RosterCandidateFavoritesProps,
} from './RosterCandidateFavorites'

interface CandidateDrawerProps {
    rosterCandidateId?: string
    onClose?: () => void
}

export default function CandidateDrawer({
    rosterCandidateId: rosterCandidateIdProp,
    onClose: onCloseProp,
}: CandidateDrawerProps) {
    const { currentRosterCandidateId, setCurrentRosterCandidateId } =
        useRosterTable()
    const { client } = useIdentity()

    const rosterCandidateId = rosterCandidateIdProp ?? currentRosterCandidateId
    const { data } = useQuery(GetCandidateForClientProfile, {
        variables: { rosterCandidateId: rosterCandidateId ?? '' },
        skip: !rosterCandidateId || rosterCandidateId === '',
    })

    const currentCandidate = data?.rosterCandidate?.candidate
    const clientFavoritesProps = mapDataToRosterCandidateFavoritesInput(data)
    const onClose =
        onCloseProp ??
        (() => {
            setCurrentRosterCandidateId()
        })
    const recruiterNote = data?.rosterCandidate?.externalRecruiterNote
    return (
        <Drawer
            title={'candidate'}
            open={rosterCandidateId !== undefined && rosterCandidateId !== ''}
            onClose={onClose}
            anchor="right"
        >
            <Stack
                direction="column"
                gap={getPxAsRem(20)}
                sx={{
                    padding: getPxAsRem(24),
                    width: getPxAsRem(550),
                    maxWidth: '45rem',
                    flexGrow: 1,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        marginBottom: getPxAsRem(20),
                    }}
                >
                    <Typography variant="h3">Candidate Details</Typography>
                    <IconButton onClick={onClose} aria-label="close">
                        <Close />
                    </IconButton>
                </Stack>
                <CandidateDetails data={data ?? null} />

                {/**
                 * BIO
                 */}
                <Accordion label="Bio" isExpanded showBorder={false}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {currentCandidate?.bio?.bio || 'No bio available.'}
                    </Typography>
                </Accordion>

                {/**
                 * RESUME
                 */}

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={getPxAsRem(8)}
                    padding={getPxAsRem(20)}
                >
                    <DownloadResume
                        documents={currentCandidate?.documents ?? []}
                    />
                </Stack>

                <Accordion
                    label="In These Searches"
                    isExpanded
                    showBorder={false}
                >
                    <OtherSearchesForCandidate
                        candidateId={currentCandidate?.id ?? ''}
                    />
                </Accordion>

                {/**
                 * FAVORITES
                 */}
                <Accordion label="Favorites" isExpanded showBorder={false}>
                    <RosterCandidateFavorites {...clientFavoritesProps} />
                </Accordion>

                {/**
                 * NOTES
                 */}
                <Accordion
                    label="Process Summary"
                    isExpanded
                    showBorder={false}
                >
                    <Stack direction={'column'} spacing={2}>
                        <Typography
                            variant="body1"
                            sx={{ whiteSpace: 'pre-line' }}
                        >
                            {recruiterNote?.note || 'No notes available'}
                        </Typography>

                        <Typography variant="detail">
                            {getAuditLabel(
                                recruiterNote?.createdAt,
                                recruiterNote?.updatedAt,
                                recruiterNote?.updatedByUser?.preferredName ??
                                    undefined,
                                recruiterNote?.createdByUser?.preferredName ??
                                    undefined
                            )}
                        </Typography>
                    </Stack>
                </Accordion>
                <ClientNotes
                    rosterCandidateId={rosterCandidateId ?? ''}
                    disabled={!client}
                />
            </Stack>
        </Drawer>
    )
}

function mapDataToRosterCandidateFavoritesInput(
    data?: GetRosterCandidateForClientProfileQuery
): RosterCandidateFavoritesProps {
    return {
        clientFavorites:
            data?.rosterCandidate?.clientFavorites?.map((favorite) => ({
                id: favorite.id,
                user: favorite.client?.user
                    ? {
                          firstName: favorite.client.user.firstName,
                          nickname: favorite.client.user.nickname || undefined,
                          lastName: favorite.client.user.lastName,
                      }
                    : undefined,
                updatedAt: favorite.updatedAt,
                createdAt: favorite.createdAt,
            })) || [],
    }
}
